import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can } from '@/shared/screen/define';

import { operatorListFilters } from './bank-account-tabs.filters';
import { ACTIVATION_CHANNELS } from './app-access.wire';

// Ações das abas Operadores, Acesso ao aplicativo e Credores (U23/U24), acrescentadas ao manifesto do
// detalhe. Leve: o fio entra pela tela.
//
// O que **não** entra aqui, de propósito: as ações que só existem presas a uma linha da tabela
// (Editar, Excluir e Cancelar operador; Detalhes da aprovação; Liberar e Revogar conta no aplicativo;
// Adicionar e Excluir credor). Todas precisariam de uma chave de alvo (`id`, `personId`) na entrada,
// que é exatamente o que a trava `src/app/agent-tool-targets.test.ts` recusa, e nenhuma delas tem um
// "registro aberto" a que se prender. Elas continuam na tela, pelo `ActionDialog` (relatório U5-U7).

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
const OPEN_ACCOUNT_ONLY = 'This action only runs on the bank account detail screen, on the account that is open there.';
const needsOpenAccount = () => {
  throw new Error(OPEN_ACCOUNT_ONLY);
};
const none = z.strictObject({});

// ─── Operadores ───────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`OperatorHeader.tsx:60-66`): a lista de operadores e o saldo da conta (`:31-35`). */
export const operatorsRefresh = action({
  id: 'bankAccount.operators.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de operadores e o saldo da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

export const operatorsFilter = filterAction('bankAccount.operators.filter', operatorListFilters, {
  description: msg`Filtra a lista de operadores da conta aberta: searchString (todos os campos), f_userUserId (nome do operador), f_level (Viewer, JointApprover, MasterApprover) e f_status (Approved, Pending, Rejected, Timeout, Canceled, Error, Processing, Finished). Filtro omitido é limpo.`,
});

export const operatorsPage = listPageAction('bankAccount.operators.page');

/**
 * "Aprovar" e "Reprovar" (`OperatorHeader.tsx:46-59`, `OperatorList.tsx:118-129`): pedem senha e
 * código MFA (F2a). `stepUp: true` ⇒ o `run` **só prepara** o diálogo; quem executa é a pessoa (U24).
 * Agem sobre as linhas marcadas na tabela, nunca sobre um alvo escrito pelo agente.
 */
const approvalAction = (id: string, label: MessageDescriptor, kind: 'write' | 'destructive', description: MessageDescriptor, title: MessageDescriptor) =>
  action({
    id,
    kind,
    label,
    stepUp: true,
    description,
    input: none,
    preview: () => ({ title: i18n._(title), detail: i18n._(msg`Confirme com a sua senha e o código do aplicativo MFA.`), ...(kind === 'destructive' ? { tone: 'destructive' as const } : {}) }),
    run: needsOpenAccount,
  });

export const operatorsApprove = approvalAction(
  'bankAccount.operators.approve',
  msg`Aprovar`,
  'write',
  msg`Prepara a aprovação dos operadores marcados na lista de operadores da conta aberta. Só abre o diálogo de senha e código MFA: esta ferramenta não aprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela.`,
  msg`Aprovar operador`,
);

export const operatorsReject = approvalAction(
  'bankAccount.operators.reject',
  msg`Reprovar`,
  'destructive',
  msg`Prepara a reprovação dos operadores marcados na lista de operadores da conta aberta. Só abre o diálogo de senha e código MFA: esta ferramenta não reprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela.`,
  msg`Reprovar operador`,
);

// ─── Acesso ao aplicativo ─────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`AppAccessHeader.tsx:51-57`). */
export const appAccessRefresh = action({
  id: 'bankAccount.appAccess.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o acesso ao aplicativo do titular e a lista de contas que podem ser liberadas. Nada é alterado.`,
  run: () => undefined,
});

export const appAccessPage = listPageAction('bankAccount.appAccess.page');

const GRANT = can('BankAccount', 'RequestNewOperator', { strict: true });
const UPDATE = can('BankAccount', 'Update');

/** "Conceder acesso" (`AppAccessHeader.tsx:58-64`): o canal é o único campo (`GrantAppAccessForm.tsx:24-32`). */
export const appAccessGrant = action({
  id: 'bankAccount.appAccess.grant',
  kind: 'write',
  label: msg`Conceder acesso`,
  description: msg`Concede ao titular da conta aberta o acesso ao aplicativo do cliente e envia o convite de ativação por e-mail (Email) ou telefone (Phone), para o contato que está no cadastro da pessoa. A pessoa é sempre o titular da conta aberta e não pode ser escolhida.`,
  permission: GRANT,
  input: z.strictObject({ activationChannelType: z.enum(ACTIVATION_CHANNELS) }),
  preview: () => ({ title: i18n._(msg`Conceder acesso ao aplicativo`), detail: i18n._(msg`A pessoa vai receber um convite para ativar o acesso ao aplicativo.`) }),
  run: needsOpenAccount,
});

/** "Reenviar convite" (`AppAccessHeader.tsx:65-71`): só com o acesso pendente. */
export const appAccessResend = action({
  id: 'bankAccount.appAccess.resend',
  kind: 'write',
  label: msg`Reenviar convite`,
  description: msg`Envia de novo o convite de ativação ao titular da conta aberta. Só enquanto o acesso está pendente. Não recebe entrada.`,
  permission: GRANT,
  input: none,
  preview: () => ({ title: i18n._(msg`Reenviar convite`), detail: i18n._(msg`Deseja enviar de novo o convite de ativação para esta pessoa?`) }),
  run: needsOpenAccount,
});

/** "Suspender acesso" (`AppAccessHeader.tsx:72-78`): motivo obrigatório, até 512 (`PopupAppAccessSchema.ts:3-13`). */
export const appAccessSuspend = action({
  id: 'bankAccount.appAccess.suspend',
  kind: 'write',
  label: msg`Suspender acesso`,
  description: msg`Suspende o acesso ao aplicativo do titular da conta aberta, com um motivo (até 512 caracteres). A pessoa deixa de usar o aplicativo até o acesso ser retomado.`,
  permission: UPDATE,
  input: z.strictObject({ reason: z.string().min(1).max(512) }),
  preview: () => ({ title: i18n._(msg`Suspender acesso`), detail: i18n._(msg`A pessoa deixa de usar o aplicativo até o acesso ser retomado. Informe o motivo.`) }),
  run: needsOpenAccount,
});

/** "Retomar acesso" (`AppAccessHeader.tsx:79-85`): corpo `{}`. */
export const appAccessResume = action({
  id: 'bankAccount.appAccess.resume',
  kind: 'write',
  label: msg`Retomar acesso`,
  description: msg`Retoma o acesso ao aplicativo suspenso do titular da conta aberta. Não recebe entrada.`,
  permission: UPDATE,
  input: none,
  preview: () => ({ title: i18n._(msg`Retomar acesso`), detail: i18n._(msg`O acesso volta a valer e a pessoa consegue usar o aplicativo de novo. Confirma?`) }),
  run: needsOpenAccount,
});

/** "Revogar acesso" (`AppAccessHeader.tsx:86-92`): não volta atrás. */
export const appAccessRevoke = action({
  id: 'bankAccount.appAccess.revoke',
  kind: 'destructive',
  label: msg`Revogar acesso`,
  description: msg`Revoga em definitivo o acesso ao aplicativo do titular da conta aberta, com um motivo (até 512 caracteres). Não volta atrás: liberar de novo é conceder um acesso novo.`,
  permission: UPDATE,
  input: z.strictObject({ reason: z.string().min(1).max(512) }),
  preview: () => ({
    title: i18n._(msg`Revogar acesso`),
    detail: i18n._(msg`O acesso é encerrado em definitivo e não volta atrás: para liberar de novo é preciso conceder um acesso novo. Informe o motivo.`),
    tone: 'destructive' as const,
  }),
  run: needsOpenAccount,
});

// ─── Credores ─────────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`CreditorsHeader.tsx:31-37`). */
export const creditorsRefresh = action({
  id: 'bankAccount.creditors.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de credores da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

export const creditorsPage = listPageAction('bankAccount.creditors.page');

/** As ações novas do detalhe, para o `actions` do manifesto. */
export const BANK_ACCOUNT_TAB_ACTIONS = [
  operatorsRefresh,
  operatorsFilter,
  operatorsPage,
  operatorsApprove,
  operatorsReject,
  appAccessRefresh,
  appAccessPage,
  appAccessGrant,
  appAccessResend,
  appAccessSuspend,
  appAccessResume,
  appAccessRevoke,
  creditorsRefresh,
  creditorsPage,
];
